import { Input } from '@components/ui/shadcn/input';
import { faFileArrowDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import { configurationPageContentClass } from '@modules/configuration/components/configurationLayout';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useToast } from '@shared/hooks/use-toast';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCouriers, useCountries, useExportCoverageRules } from '../../hooks';
import { CourierAccordion } from '../display';
import { ErrorState } from '../feedback';
import { CoverageZonesSkeleton } from '../skeletons';
import { ExportCoverageRulesModal } from '../modals';

export const Main = () => {
  const { t } = useTranslation('coverageZonesModule');
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { couriers, isLoading, error, refetch } = useCouriers();
  const { countries } = useCountries();
  const exportMutation = useExportCoverageRules();

  const filteredCouriers = useMemo(() => {
    if (!searchTerm) return couriers;
    return couriers.filter((courier) => courier.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [couriers, searchTerm]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleExport = useCallback(() => {
    setIsExportModalOpen(true);
  }, []);

  const handleExportSubmit = useCallback(
    (data: { countryId: number; courierServiceId?: number }) => {
      exportMutation.mutate(
        {
          countryId: data.countryId,
          courierServiceId: data.courierServiceId,
        },
        {
          onSuccess: (url) => {
            if (url) {
              const link = document.createElement('a');
              link.href = url;
              link.download = '';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              toast({
                title: t('toast.exportSuccess'),
              });
            } else {
              toast({
                title: t('toast.exportError'),
                description: 'No se pudo obtener la URL del archivo',
                variant: 'destructive',
              });
            }

            setIsExportModalOpen(false);
          },
          onError: (error: any) => {
            const apiMsg = error?.response?.data?.message || error?.message || t('toast.exportError');
            toast({
              title: t('toast.exportError'),
              description: apiMsg,
              variant: 'destructive',
            });
          },
        },
      );
    },
    [exportMutation, toast, t],
  );

  usePageNavigation({
    breadcrumbs: [{ label: t('title') }],
  });

  if (isLoading) {
    return <CoverageZonesSkeleton />;
  }

  if (error) {
    return (
      <div className="w-full pb-6">
        <ErrorState error={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="w-full pb-6" data-testid="coverage-zones-view">
      <ConfigurationPageHeader
        title={t('title')}
        description={t('subtitle')}
        action={
          <Button variant="outline" onClick={handleExport} data-testid="export-coverage-rules-button">
            <span className="flex items-center gap-2">
              <FontAwesomeIcon icon={faFileArrowDown} className="h-4 w-4" />
              {t('export.export')}
            </span>
          </Button>
        }
      />

      <div className={configurationPageContentClass}>
        {couriers.length > 0 && (
          <div className="relative mb-6 max-w-md">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder={t('search')}
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 transition-colors"
            />
          </div>
        )}

        {filteredCouriers.length > 0 && (
          <div className="space-y-4">
            {filteredCouriers.map((courier) => (
              <CourierAccordion key={courier.id} courier={courier} />
            ))}
          </div>
        )}

        {filteredCouriers.length === 0 && (
          <EmptyView
            type={searchTerm ? 'noResults' : 'emptyState'}
            icon={EMPTY_VIEW_ICONS.COURIERS}
            title={searchTerm ? undefined : t('empty.noCouriers')}
            description={searchTerm ? t('empty.noResults') : 'No hay couriers configurados en el sistema'}
          />
        )}
      </div>

      <ExportCoverageRulesModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSubmit={handleExportSubmit}
        isExporting={exportMutation.isPending}
        countries={countries}
      />
    </div>
  );
};
